// ============================================
// Field Mapper — snake_case ↔ camelCase
// ============================================
// Single source of truth for naming convention
// conversion between backend (D1) and frontend (React).
//
// Backend (Worker API): snake_case
// Frontend (React):     camelCase
// ============================================

/**
 * Convert a snake_case string to camelCase.
 * "user_id" → "userId"
 * "job_type" → "jobType"
 * "auth0_sub" → "auth0Sub"
 */
export function snakeToCamel(str) {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case.
 * "userId" → "user_id"
 * "jobType" → "job_type"
 * "auth0Sub" → "auth0_sub"
 */
export function camelToSnake(str) {
  return str.replace(/[A-Z]/g, (char) => '_' + char.toLowerCase());
}

/**
 * Convert all keys of an object from snake_case to camelCase.
 * Handles nested objects and arrays recursively.
 *
 * @param {any} data — API response data (snake_case keys)
 * @returns {any} — Same data with camelCase keys
 *
 * @example
 * toCamelCase({ user_id: "123", job_type: "senda" })
 * // → { userId: "123", jobType: "senda" }
 *
 * toCamelCase([{ created_at: "2026-01-01" }])
 * // → [{ createdAt: "2026-01-01" }]
 */
export function toCamelCase(data) {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) return data.map(toCamelCase);
  if (typeof data !== 'object') return data;

  const result = {};
  for (const [key, value] of Object.entries(data)) {
    const camelKey = snakeToCamel(key);
    result[camelKey] = (typeof value === 'object' && value !== null && !(value instanceof Date))
      ? toCamelCase(value)
      : value;
  }
  return result;
}

/**
 * Convert all keys of an object from camelCase to snake_case.
 * Handles nested objects and arrays recursively.
 *
 * @param {any} data — Frontend data (camelCase keys)
 * @returns {any} — Same data with snake_case keys
 *
 * @example
 * toSnakeCase({ userId: "123", jobType: "senda" })
 * // → { user_id: "123", job_type: "senda" }
 *
 * toSnakeCase({ hasElevator: true, floorLevel: 3 })
 * // → { has_elevator: true, floor_level: 3 }
 */
export function toSnakeCase(data) {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) return data.map(toSnakeCase);
  if (typeof data !== 'object') return data;

  const result = {};
  for (const [key, value] of Object.entries(data)) {
    const snakeKey = camelToSnake(key);
    result[snakeKey] = (typeof value === 'object' && value !== null && !(value instanceof Date))
      ? toSnakeCase(value)
      : value;
  }
  return result;
}
